import axios from 'axios';
import config from '../config/environment';
import { RefereeRegistrationInfo, CompleteRefereeRegistrationRequest } from '../types/referee';
import { ContactInfoDto, hasAnyContactInfo } from '../types/contactInfo';

export { hasAnyContactInfo };

const API_BASE_URL = config.apiUrl;

export interface InviteRefereeRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  gameTypes: string[];
  isMainReferee: boolean;
}

export interface UpdateRefereeStatusRequest {
  status: string;
}

export interface RefereeDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  isEmailVerified: boolean;
  fieldAssignments: { [key: string]: string };
  gameTypes: string[];
  isMainReferee: boolean;
  rating: number;
  totalGames: number;
  contactInfo?: ContactInfoDto;
}

export interface RefereeRanksDto {
  rankId: string;
  rankName: string;
  points: number;
  iconUrl?: string;
  level: number;
  pointsRequired: number;
  nextLevelPoints: number;
  progressToNext: number;
}

// Re-export ContactInfoDto as RefereeContactInfoDto for backwards compatibility
export type RefereeContactInfoDto = ContactInfoDto;

export interface RefereeProfileDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  isEmailVerified: boolean;
  fieldAssignments: { [key: string]: string };
  gameTypes: string[];
  isMainReferee: boolean;
  rating: number;
  ratingConfirmed: number;
  totalGames: number;
  totalAirsoftGames: number;
  totalPaintballGames: number;
  totalSpeedballGames: number;
  ranks: RefereeRanksDto;
  achievementsIds: string[];
  assignedFields: any[]; // FieldDto
  contactInfo?: RefereeContactInfoDto;
}

export interface RefereeGamesResponse {
  games: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}

export interface RefereeLoginRequest {
  email: string;
  password: string;
}

export interface RefereeLoginResponse {
  token: string;
  refreshToken: string;
  referee: RefereeDto;
}

export interface UpdateRefereeProfileRequest {
  firstName: string;
  lastName: string;
  phone: string;
  contactInfo: ContactInfoDto;
}

class RefereeService {
  private getAuthHeaders() {
    const token = localStorage.getItem('fieldToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  private getRefereeAuthHeaders() {
    const token = localStorage.getItem('refereeToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async inviteReferee(request: InviteRefereeRequest): Promise<RefereeDto> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/referee/invite`, request, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to invite referee');
    }
  }

  async getFieldReferees(): Promise<RefereeDto[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/referee/field-referees`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to get field referees');
    }
  }

  async updateRefereeStatus(refereeId: string, request: UpdateRefereeStatusRequest): Promise<void> {
    try {
      await axios.put(`${API_BASE_URL}/api/referee/${refereeId}/status`, request, {
        headers: this.getAuthHeaders()
      });
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to update referee status');
    }
  }

  async removeRefereeFromField(refereeId: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/referee/${refereeId}`, {
        headers: this.getAuthHeaders()
      });
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to remove referee from field');
    }
  }

  async setMainReferee(refereeId: string, isMain: boolean): Promise<void> {
    try {
      await axios.put(`${API_BASE_URL}/api/referee/${refereeId}/main-referee`, { isMain }, {
        headers: this.getAuthHeaders()
      });
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to update main referee status');
    }
  }

  async loginReferee(request: RefereeLoginRequest): Promise<RefereeLoginResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/referee/login`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to login referee');
    }
  }

  async getRegistrationInfo(token: string): Promise<RefereeRegistrationInfo> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/referee/registration-info?token=${token}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to get registration info');
    }
  }

  async completeRegistration(request: CompleteRefereeRegistrationRequest): Promise<{ message: string }> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/referee/complete-registration`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to complete registration');
    }
  }

  async getPublicFieldReferees(fieldId: string): Promise<RefereeDto[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/referee/public/field/${fieldId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to get field referees');
    }
  }

  async getRefereeProfile(refereeId?: string): Promise<RefereeProfileDto> {
    try {
      if (refereeId) {
        // Публичный режим - используем query параметр
        const response = await axios.get(`${API_BASE_URL}/api/referee/profile?refereeId=${refereeId}`);
        return response.data;
      } else {
        // Приватный режим - используя авторизацию
        const response = await axios.get(`${API_BASE_URL}/api/referee/profile`, {
          headers: this.getRefereeAuthHeaders()
        });
        return response.data;
      }
    } catch (error: any) {
      throw new Error(error.response?.data || 'Failed to get referee profile');
    }
  }

  async getRefereeGames(
    refereeId: string,
    gameType: 'airsoft' | 'paintball' | 'speedball',
    options?: {
      page?: number;
      pageSize?: number;
      specificDate?: string;
    }
  ): Promise<RefereeGamesResponse> {
    try {
      const params = new URLSearchParams();

      if (options?.page) params.append('page', options.page.toString());
      if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
      if (options?.specificDate) params.append('specificDate', options.specificDate);

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/referee/${refereeId}/games/${gameType}${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || `Failed to get referee ${gameType} games`);
    }
  }

  async updateContactInfo(contactInfo: RefereeContactInfoDto): Promise<void> {
    try {
      await axios.put(`${API_BASE_URL}/api/referee/contact-info`, contactInfo, {
        headers: this.getRefereeAuthHeaders()
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update contact info');
    }
  }

  async updateRefereeProfile(request: UpdateRefereeProfileRequest): Promise<void> {
    try {
      await axios.put(`${API_BASE_URL}/api/referee/profile`, request, {
        headers: this.getRefereeAuthHeaders()
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }
}

export default new RefereeService();